import { useCallback } from 'react';
import { ListTasksResponse } from '@weave/schema-gen-ts/dist/schemas/task-center/v1/task_service.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { QueryKey, useQueryClient } from 'react-query';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { CallInsightApi } from './use-call-insight-controller';

type UseTaskHandlersProps = {
  queryKey: QueryKey; // The query key for the tasks list
  emitEvent: CallInsightApi['emitEvent'];
  updateCallInsight: (args: { updatedCallIntelInsights: Call }) => void;
};

export const useTaskHandlers = ({ emitEvent, queryKey, updateCallInsight }: UseTaskHandlersProps) => {
  const queryClient = useQueryClient();

  // Handler for updating all tasks
  const handleTasksUpdate = useCallback(
    (call: CallIntelTypes.CallRecord, updatedTasks: CallIntelTypes.Task[]) => {
      queryClient.setQueryData<ListTasksResponse>(queryKey, (prev) => ({
        ...(prev || { cursor: undefined }),
        tasks: updatedTasks,
      }));
      emitEvent('tasksUpdated', { call: call as Call, tasks: updatedTasks });
    },
    [emitEvent, queryClient, queryKey]
  );

  // Handler for deleting a single task
  const handleTaskDelete = useCallback(
    (call: CallIntelTypes.CallRecord, taskId: string) => {
      const prev = queryClient.getQueryData<ListTasksResponse>(queryKey);
      if (!prev) return;

      const remainingTasks = (prev.tasks || []).filter((task) => task.id !== taskId);

      queryClient.setQueryData<ListTasksResponse>(queryKey, {
        ...prev,
        tasks: remainingTasks,
      });

      const remainingTypes = Array.from(new Set(remainingTasks.map((t) => t.type))) as CallIntelTypes.TaskType[];

      const updatedCall = {
        ...call,
        taskCount: call.taskCount ? Math.max(call.taskCount - 1, 0) : 0,
        taskTypes: remainingTypes,
      } as Call;

      updateCallInsight({
        updatedCallIntelInsights: updatedCall,
      });

      emitEvent('taskDeleted', { call: updatedCall, taskId });
    },
    [emitEvent, queryClient, queryKey, updateCallInsight]
  );

  return {
    handleTasksUpdate,
    handleTaskDelete,
  };
};
